import React, { useEffect, useState, useContext, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Play, Pause, CheckCircle2, Package, Clock, ClipboardList, Wrench, AlertCircle, Receipt, Eye, Info, Upload, PenLine, ChevronRight, ChevronLeft, X, Camera } from 'lucide-react';
import { toast } from 'sonner';
import BuildingFilter from '../components/BuildingFilter';

const STATUS_PILL = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  assigned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  in_progress: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  on_hold: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200',
  completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  verified: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
  closed: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200',
  cancelled: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
};

const PRIORITY_PILL = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  critical: 'bg-rose-100 text-rose-700',
};

export default function WorkOrders() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [wos, setWos] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBuildingId, setFilterBuildingId] = useState('all');
  const [filterExec, setFilterExec] = useState('all');
  // Phase 35c — role-aware "assigned to me" filter (defaults ON for TECHNICIAN)
  const [scopeMine, setScopeMine] = useState(
    params.get('scope') === 'mine' || (user?.role === 'TECHNICIAN' && !params.get('scope'))
  );
  const [showNew, setShowNew] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [completingWo, setCompletingWo] = useState(null);
  const [matReqFor, setMatReqFor] = useState(null); // Feb-2026: compact MR dialog directly from row
  const [draft, setDraft] = useState(initialDraft());
  const [saving, setSaving] = useState(false);

  function initialDraft() {
    return {
      title: '', description: '', category: 'maintenance', priority: 'medium',
      building_id: '', asset_id: '', executor_type: 'internal', assigned_user_ids: [],
      service_provider_id: '', scheduled_start: '', scheduled_end: '',
      sla_target_hours: '',
      linked_complaint_id: '',
    };
  }

  useEffect(() => { fetchAll(); }, [filterStatus, filterBuildingId, filterExec, scopeMine]);

  // Phase 22 — when navigating from /scan with ?building=...&asset=...,
  // auto-open the New WO dialog pre-filled with the scanned context.
  // Runs on first mount only.
  useEffect(() => {
    const bId = params.get('building');
    const aId = params.get('asset');
    if (bId) {
      setDraft((d) => ({ ...d, building_id: bId, asset_id: aId || '' }));
      setShowNew(true);
    }
  }, []);

  // Auto-open a specific WO when arriving from Complaints "View Work Order" pill.
  // Waits until the list has loaded so the target WO is available in state.
  useEffect(() => {
    const h = params.get('highlight');
    if (h && wos.length > 0) {
      const target = wos.find(w => w.id === h);
      if (target) setViewing(target);
    }
  }, [params, wos]);

  const fetchAll = async () => {
    try {
      const q = new URLSearchParams();
      if (filterStatus !== 'all') q.append('status', filterStatus);
      if (filterBuildingId !== 'all') q.append('building_id', filterBuildingId);
      if (filterExec !== 'all') q.append('executor_type', filterExec);
      if (scopeMine && user?.id) q.append('assigned_user_id', user.id);
      const [w, b, s, st, c] = await Promise.all([
        axios.get(`${API}/work-orders?${q.toString()}`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/suppliers`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/staff`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/complaints`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setWos(w.data || []);
      setBuildings(b.data || []);
      setSuppliers(s.data || []);
      setStaff(st.data || []);
      setComplaints(c.data || []);
    } catch (e) { toast.error('Failed to load work orders'); }
  };

  const createWO = async () => {
    if (!draft.title || !draft.building_id) {
      return toast.error('Title and Building are required');
    }
    if (draft.executor_type === 'service_provider' && !draft.service_provider_id) {
      return toast.error('Pick a service provider');
    }
    setSaving(true);
    try {
      const { linked_complaint_id, ...rest } = draft;
      const payload = {
        ...rest,
        sla_target_hours: draft.sla_target_hours ? parseFloat(draft.sla_target_hours) : null,
        scheduled_start: draft.scheduled_start || null,
        scheduled_end: draft.scheduled_end || null,
        service_provider_id: draft.executor_type === 'service_provider' ? draft.service_provider_id : null,
        assigned_user_ids: draft.executor_type === 'internal' ? draft.assigned_user_ids : [],
        ...(linked_complaint_id ? { source: 'complaint', source_id: linked_complaint_id } : {}),
      };
      const r = await axios.post(`${API}/work-orders`, payload, { withCredentials: true });
      toast.success(`Created ${r.data.wo_number}`);
      setShowNew(false);
      setDraft(initialDraft());
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  const act = async (id, kind, body = {}) => {
    try {
      const r = await axios.post(`${API}/work-orders/${id}/${kind}`, body, { withCredentials: true });
      toast.success(kind.replace('-', ' ') + ' OK');
      setViewing(r.data);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || `Action ${kind} failed`); }
  };

  const billTenant = async (id) => {
    try {
      const r = await axios.post(`${API}/work-orders/${id}/bill-tenant`, {}, { withCredentials: true });
      toast.success(`Draft invoice created: ${r.data.invoice_id.slice(0, 8)} (${r.data.amount})`);
      fetchAll();
      setViewing(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Bill failed'); }
  };

  const summary = useMemo(() => {
    const s = { total: wos.length, in_progress: 0, completed: 0, overdue: 0, totalCost: 0 };
    const now = new Date();
    wos.forEach(w => {
      if (w.status === 'in_progress') s.in_progress++;
      if (['completed', 'verified', 'closed'].includes(w.status)) s.completed++;
      if (w.scheduled_end && w.status !== 'completed' && new Date(w.scheduled_end) < now) s.overdue++;
      s.totalCost += (w.cost_total || 0);
    });
    return s;
  }, [wos]);

  const buildingName = (id) => buildings.find(b => b.id === id)?.name || '—';
  const supplierLabel = (id) => {
    const s = suppliers.find(x => x.id === id);
    return s ? `${s.code || '?'} · ${s.name}` : '—';
  };

  return (
    <Layout>
      <div data-testid="work-orders-page" className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2"><ClipboardList className="w-7 h-7 text-amber-500" />Work Orders</h1>
            <p className="text-sm text-muted-foreground">Dispatch and track execution across in-house teams and service providers.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="wo-help-btn"
              className="text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 text-sm px-3 py-1.5 border rounded-md bg-white hover:bg-slate-50"
              title={
                'A Work Order (WO) is the formal instruction issued to a technician or ' +
                'service provider to execute a specific job — usually generated from a ' +
                'complaint or a PPM task. Each WO tracks:\n' +
                '• Who is doing it (in-house or external supplier)\n' +
                '• Start / pause / complete timestamps\n' +
                '• Labour hours + materials issued\n' +
                '• Cost + optional billing to the tenant\n\n' +
                'Icons on each row:\n' +
                '▶ Start   — technician begins work\n' +
                '⏸ Pause   — on hold (waiting for parts / approval)\n' +
                '✓ Complete — mark job finished (creates a Service Report)\n' +
                '👁 View    — full details, labour, materials, sign-off'
              }
            >
              <Info className="w-4 h-4" />What is a Work Order?
            </button>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild>
              <Button data-testid="new-wo-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-4 h-4 mr-1" />New Work Order</Button>
            </DialogTrigger>
            <DialogContent className="w-[98vw] sm:w-[95vw] max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="wo-create-dialog">
              <DialogHeader><DialogTitle>New Work Order</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {/* Optional link to an existing Complaint — when picked, the WO
                    is stamped as source=complaint and the backend enriches
                    title / description / category / priority / building from
                    the complaint automatically. */}
                <div className="rounded-md border border-amber-200 bg-amber-50/40 p-2">
                  <Label className="text-xs text-amber-800">Linked Complaint <span className="font-normal text-slate-500">(optional — leave blank for ad-hoc WOs)</span></Label>
                  <Select value={draft.linked_complaint_id} onValueChange={(v) => {
                    const c = complaints.find(x => x.id === v);
                    setDraft(d => ({
                      ...d,
                      linked_complaint_id: v,
                      title: d.title || c?.title || '',
                      description: d.description || c?.description || '',
                      building_id: d.building_id || c?.building_id || '',
                      category: c?.category || d.category,
                      priority: c?.priority || d.priority,
                    }));
                  }}>
                    <SelectTrigger data-testid="wo-complaint"><SelectValue placeholder="Pick an open complaint to link…" /></SelectTrigger>
                    <SelectContent>
                      {complaints.filter(c => !['closed', 'cancelled'].includes(c.status)).length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-500">No open complaints — this WO will be created as ad-hoc.</div>
                      )}
                      {complaints
                        .filter(c => !['closed', 'cancelled'].includes(c.status))
                        .map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="font-mono text-xs mr-2">{c.ticket_number || c.id.slice(0, 8)}</span>
                            {(c.title || '').slice(0, 55)}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Title *</Label><Input data-testid="wo-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></div>
                <div><Label>Description</Label><Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Building *</Label>
                    <Select value={draft.building_id} onValueChange={(v) => setDraft({ ...draft, building_id: v })}>
                      <SelectTrigger data-testid="wo-building"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Category</Label>
                    <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="cleaning">Cleaning</SelectItem>
                        <SelectItem value="electrical">Electrical</SelectItem>
                        <SelectItem value="plumbing">Plumbing</SelectItem>
                        <SelectItem value="hvac">HVAC</SelectItem>
                        <SelectItem value="inspection">Inspection</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Priority</Label>
                    <Select value={draft.priority} onValueChange={(v) => setDraft({ ...draft, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Executor *</Label>
                    <Select value={draft.executor_type} onValueChange={(v) => setDraft({ ...draft, executor_type: v })}>
                      <SelectTrigger data-testid="wo-executor"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">Internal Team</SelectItem>
                        <SelectItem value="service_provider">Service Provider</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {draft.executor_type === 'service_provider' && (
                    <div className="sm:col-span-2"><Label>Service Provider *</Label>
                      <Select value={draft.service_provider_id} onValueChange={(v) => setDraft({ ...draft, service_provider_id: v })}>
                        <SelectTrigger data-testid="wo-sp"><SelectValue placeholder="Pick provider" /></SelectTrigger>
                        <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{(s.code || '?')} · {s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {draft.executor_type === 'internal' && staff.length > 0 && (
                    <div className="sm:col-span-2"><Label>Assigned Staff</Label>
                      <Select value={draft.assigned_user_ids[0] || ''} onValueChange={(v) => setDraft({ ...draft, assigned_user_ids: v ? [v] : [] })}>
                        <SelectTrigger data-testid="wo-staff"><SelectValue placeholder="Pick technician" /></SelectTrigger>
                        <SelectContent>{staff.map(s => <SelectItem key={s.id} value={s.user_id || s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Scheduled Start</Label><Input type="datetime-local" value={draft.scheduled_start} onChange={(e) => setDraft({ ...draft, scheduled_start: e.target.value })} /></div>
                  <div><Label>Scheduled End</Label><Input type="datetime-local" value={draft.scheduled_end} onChange={(e) => setDraft({ ...draft, scheduled_end: e.target.value })} /></div>
                  <div><Label>SLA Target (hours)</Label><Input type="number" step="0.5" value={draft.sla_target_hours} onChange={(e) => setDraft({ ...draft, sla_target_hours: e.target.value })} /></div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                  <Button data-testid="save-wo-btn" disabled={saving} onClick={createWO} className="bg-amber-500 hover:bg-amber-600 text-slate-900">{saving ? 'Saving…' : 'Create'}</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-4"><div className="text-xs text-muted-foreground uppercase tracking-wide">Total WOs</div><div className="text-2xl font-bold mt-1">{summary.total}</div></Card>
          <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200"><div className="text-xs text-amber-700 uppercase tracking-wide">In Progress</div><div className="text-2xl font-bold mt-1 text-amber-900">{summary.in_progress}</div></Card>
          <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200"><div className="text-xs text-emerald-700 uppercase tracking-wide">Completed</div><div className="text-2xl font-bold mt-1 text-emerald-900">{summary.completed}</div></Card>
          <Card className="p-4 bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200"><div className="text-xs text-rose-700 uppercase tracking-wide">Overdue</div><div className="text-2xl font-bold mt-1 text-rose-900">{summary.overdue}</div></Card>
        </div>

        {/* Filters */}
        <Card className="p-3 flex flex-wrap gap-2 items-end">
          <BuildingFilter value={filterBuildingId} onChange={setFilterBuildingId} buildings={buildings} />
          {user?.role !== 'TENANT' && (
            <Button
              data-testid="wo-scope-mine-toggle"
              type="button"
              variant={scopeMine ? 'default' : 'outline'}
              size="sm"
              className={`h-9 ${scopeMine ? 'bg-teal-600 hover:bg-teal-700 text-white' : ''}`}
              onClick={() => setScopeMine(v => !v)}
              title="Show only work orders assigned to me"
            >
              <CheckCircle2 className={`w-3.5 h-3.5 mr-1 ${scopeMine ? 'opacity-100' : 'opacity-30'}`} />
              Assigned to me
            </Button>
          )}
          <div className="flex flex-col"><Label className="text-xs mb-1">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-9 w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col"><Label className="text-xs mb-1">Executor</Label>
            <Select value={filterExec} onValueChange={setFilterExec}>
              <SelectTrigger className="h-9 w-[150px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Both</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="service_provider">Service Provider</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* List */}
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-slate-50 dark:bg-slate-900/50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">WO #</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left">Building</th>
                <th className="px-3 py-2 text-left">Executor</th>
                <th className="px-3 py-2 text-left">Priority</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Cost</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {wos.length === 0 && (<tr><td colSpan={8} className="text-center text-muted-foreground py-10">No work orders yet — click &quot;New Work Order&quot; to dispatch one.</td></tr>)}
              {wos
                .slice()
                // Feb-2026 — sort by (a) status priority: active first,
                // completed/closed/cancelled at bottom; (b) within a group,
                // newest on top so operators find fresh WOs instantly.
                .sort((a, b) => {
                  const closedRank = (s) => (['completed', 'closed', 'cancelled', 'verified'].includes(s) ? 1 : 0);
                  const ra = closedRank(a.status);
                  const rb = closedRank(b.status);
                  if (ra !== rb) return ra - rb;
                  return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
                })
                .map((w, idx) => (
                <tr key={w.id} className="border-t hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                  <td className="px-3 py-2 font-mono text-xs">
                    <div className="flex items-center gap-1">
                      <span data-testid={`wo-number-${idx}`}>{w.wo_number}</span>
                      {w.complaint_ticket_number && (
                        <a
                          href={`/complaints?highlight=${w.source_id}`}
                          className="text-amber-600 dark:text-amber-300 text-[10px] hover:underline"
                          title="Open this complaint in the Command Center"
                          data-testid={`wo-open-complaint-${idx}`}
                        >
                          [{w.complaint_ticket_number}] ↗
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2"><div className="font-medium">{w.title}</div><div className="text-[11px] text-slate-500">{w.source}</div></td>
                  <td className="px-3 py-2">{buildingName(w.building_id)}</td>
                  <td className="px-3 py-2">
                    {w.executor_type === 'internal' ? (
                      <span className="text-xs">INT</span>
                    ) : (
                      <span className="text-xs font-mono">{w.service_provider_code || supplierLabel(w.service_provider_id)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${PRIORITY_PILL[w.priority] || ''}`}>{w.priority}</span></td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_PILL[w.status] || ''}`}>{w.status}</span></td>
                  <td className="px-3 py-2 text-right">{(w.cost_total || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {w.status === 'assigned' && <Button data-testid={`wo-start-${idx}`} size="sm" variant="outline" title="Start work — timer begins" onClick={() => act(w.id, 'start')}><Play className="w-3 h-3" /></Button>}
                      {w.status === 'in_progress' && <Button size="sm" variant="outline" title="Pause — put job on hold (waiting for parts / approval)" onClick={() => act(w.id, 'pause')}><Pause className="w-3 h-3" /></Button>}
                      {['assigned', 'in_progress', 'on_hold'].includes(w.status) && (
                        <Button
                          data-testid={`wo-request-material-${idx}`}
                          size="sm"
                          variant="outline"
                          title="Request material from store — quick dialog (no need to open the full WO)"
                          className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:text-purple-300"
                          onClick={() => setMatReqFor(w)}
                        ><Package className="w-3 h-3 mr-1" /><span className="hidden sm:inline">Material</span></Button>
                      )}
                      {['in_progress', 'on_hold', 'assigned'].includes(w.status) && <Button data-testid={`wo-complete-${idx}`} size="sm" disabled={completingWo?.id === w.id} title="Complete work → Generate Service Report" className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40 disabled:cursor-not-allowed" onClick={() => setCompletingWo(w)}><CheckCircle2 className="w-3 h-3 mr-1" /><span className="hidden sm:inline">{completingWo?.id === w.id ? '…' : 'Complete'}</span></Button>}
                      <Button data-testid={`wo-view-${idx}`} size="sm" variant="ghost" title="View full details — labour, materials, sign-off" onClick={() => setViewing(w)}><Eye className="w-3 h-3" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* View dialog */}
        {viewing && (
          <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
            <DialogContent className="w-[98vw] sm:w-[95vw] max-w-7xl max-h-[95vh] overflow-y-auto" data-testid="wo-view-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><span className="font-mono text-base">{viewing.wo_number}</span><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_PILL[viewing.status] || ''}`}>{viewing.status}</span></DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="font-semibold text-lg">{viewing.title}</div>
                {viewing.description && <p className="text-sm text-muted-foreground">{viewing.description}</p>}
                {viewing.complaint_ticket_number && <div className="text-xs"><span className="text-muted-foreground">Linked Complaint:</span> <span className="font-mono text-amber-700">{viewing.complaint_ticket_number}</span></div>}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs text-muted-foreground">Building</div>{buildingName(viewing.building_id)}</div>
                  <div><div className="text-xs text-muted-foreground">Executor</div>{viewing.executor_type === 'internal' ? 'Internal' : supplierLabel(viewing.service_provider_id)}</div>
                  {/* Bug fix Feb-17 — always show the real timestamps, whether or not a SLA target is set. */}
                  <div data-testid="wo-view-started"><div className="text-xs text-muted-foreground">Work started</div>{viewing.actual_start ? new Date(viewing.actual_start).toLocaleString() : <span className="text-slate-400 italic">Not started yet — press ▶ to begin</span>}</div>
                  <div data-testid="wo-view-completed"><div className="text-xs text-muted-foreground">Work completed</div>{viewing.actual_end ? new Date(viewing.actual_end).toLocaleString() : <span className="text-slate-400 italic">In progress / not completed</span>}</div>
                  <div><div className="text-xs text-muted-foreground">Actual hours</div>
                    <strong className="text-slate-800 dark:text-slate-100">{viewing.sla_actual_hours != null ? `${viewing.sla_actual_hours.toFixed(2)} h` : '—'}</strong>
                    {viewing.sla_target_hours != null && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        (target {viewing.sla_target_hours}h {viewing.sla_met === true && <span className="text-emerald-600">✓ within SLA</span>}{viewing.sla_met === false && <span className="text-rose-600">✗ over SLA</span>})
                      </span>
                    )}
                  </div>
                  <div><div className="text-xs text-muted-foreground">Total Cost</div>{(viewing.cost_total || 0).toFixed(2)}</div>
                </div>

                {/* Green completion banner — captures the real completion regardless of SLA fill */}
                {viewing.status === 'completed' && (
                  <div data-testid="wo-completion-banner" className="p-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700/40 text-sm">
                    <div className="font-semibold text-emerald-900 dark:text-emerald-200 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Work completed</div>
                    <div className="text-xs text-emerald-800 dark:text-emerald-300 mt-0.5">
                      {viewing.actual_end && `Finished ${new Date(viewing.actual_end).toLocaleString()}`}
                      {viewing.sla_actual_hours != null && ` · ${viewing.sla_actual_hours.toFixed(2)} h of work`}
                      {viewing.completion_notes && ` · Notes: ${viewing.completion_notes}`}
                    </div>
                  </div>
                )}

                {viewing.labour_entries?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Labour</div>
                    <ul className="text-sm space-y-1">
                      {viewing.labour_entries.map((le, i) => <li key={i}>· {le.user_name || le.user_id || '—'}: {le.hours}h × {le.hourly_rate}/h = {(le.hours * le.hourly_rate).toFixed(2)}</li>)}
                    </ul>
                  </div>
                )}
                {viewing.materials_issued?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-1">Materials Issued (on this WO)</div>
                    <ul className="text-sm space-y-1">
                      {viewing.materials_issued.map((m, i) => <li key={i}>· {m.item_name} — {m.quantity} {m.unit} × {(m.unit_cost || 0).toFixed(2)} = {((m.quantity || 0) * (m.unit_cost || 0)).toFixed(2)}</li>)}
                    </ul>
                  </div>
                )}
                {/* Bug fix Feb-17 — surface materials from the linked Service Report so the WO
                    is a single pane of glass, not a partial view. */}
                <LinkedReportMaterials workOrder={viewing} />

                {/* Bug fix Feb-17 — prominent Start/Pause/Complete controls inside the view
                    dialog so the field user doesn't have to close the modal to commence work.
                    SLA target hours are OPTIONAL; actual start/end are captured regardless. */}
                {['assigned', 'in_progress', 'on_hold'].includes(viewing.status) && (
                  <div data-testid="wo-view-lifecycle-controls" className="flex flex-wrap gap-2 border rounded-lg p-3 bg-slate-50 dark:bg-slate-800/50">
                    {viewing.status === 'assigned' && (
                      <Button data-testid="wo-view-start-btn" onClick={() => act(viewing.id, 'start')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Play className="w-4 h-4 mr-1" />Start Work — commence &amp; capture actual start
                      </Button>
                    )}
                    {viewing.status === 'in_progress' && (
                      <Button data-testid="wo-view-pause-btn" onClick={() => act(viewing.id, 'pause')} variant="outline">
                        <Pause className="w-4 h-4 mr-1" />Pause
                      </Button>
                    )}
                    {['in_progress', 'on_hold', 'assigned'].includes(viewing.status) && (
                      <Button data-testid="wo-view-complete-btn"
                              disabled={!!completingWo}
                              onClick={() => setCompletingWo(viewing)}
                              className="bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-40 disabled:cursor-not-allowed">
                        <CheckCircle2 className="w-4 h-4 mr-1" />{completingWo ? 'Completing…' : 'Complete & Create Service Report'}
                      </Button>
                    )}
                    <span className="text-[11px] text-slate-500 self-center ml-auto">SLA target hours are optional. Actual start/end are always captured on Start/Complete.</span>
                  </div>
                )}

                {/* Quick add labour / material */}
                {['assigned', 'in_progress', 'on_hold'].includes(viewing.status) && (
                  <QuickCapture wo={viewing} onChanged={(w) => { setViewing(w); fetchAll(); }} />
                )}

                {/* Material Request — technician raises request from store, not on Service Report.
                    Visible for every active status so technicians can pre-plan material
                    before starting, and engineers can preload before assigning. */}
                {!['completed', 'cancelled', 'closed'].includes(viewing.status) && (
                  <MaterialRequestPanel wo={viewing} />
                )}

                {/* Bill tenant */}
                {viewing.status === 'completed' && !viewing.invoice_id && viewing.cost_total > 0 && (
                  <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-semibold text-amber-900 dark:text-amber-200">Recoverable cost: {viewing.cost_total.toFixed(2)}</div>
                        <div className="text-xs text-amber-700 dark:text-amber-300">Create a draft invoice for the tenant.</div>
                      </div>
                      <Button data-testid="bill-tenant-btn" onClick={() => billTenant(viewing.id)} className="bg-amber-600 hover:bg-amber-700 text-white"><Receipt className="w-4 h-4 mr-1" />Bill Tenant</Button>
                    </div>
                  </div>
                )}
                {viewing.invoice_id && (
                  <div className="p-2 rounded bg-emerald-50 dark:bg-emerald-900/20 text-xs text-emerald-800 dark:text-emerald-200">Invoiced. Draft invoice id: <span className="font-mono">{viewing.invoice_id.slice(0, 8)}</span></div>
                )}
                {viewing.service_report_id && (
                  <div className="p-2 rounded bg-sky-50 dark:bg-sky-900/20 text-xs text-sky-800 dark:text-sky-200">Service report auto-generated · id <span className="font-mono">{viewing.service_report_id.slice(0, 8)}</span></div>
                )}

                <div className="flex justify-end pt-2"><Button variant="outline" onClick={() => setViewing(null)}>Close</Button></div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Complete Work Order Wizard — guided 3-step flow so technicians
            capture completion notes, photos, and (when tenant-facing) the
            tenant's signature in a single, auditable submit. */}
        {completingWo && (
          <CompleteWorkOrderWizard
            wo={completingWo}
            onClose={() => setCompletingWo(null)}
            onSuccess={(updated) => {
              setCompletingWo(null);
              if (viewing && viewing.id === updated.id) setViewing(updated);
              fetchAll();
            }}
          />
        )}

        {/* Feb-2026 — Compact Material Request dialog opened directly from the
            row Package button. Reuses the panel so behaviour stays consistent
            with the full WO view. */}
        {matReqFor && (
          <Dialog open onOpenChange={(v) => !v && setMatReqFor(null)}>
            <DialogContent className="w-[98vw] sm:w-[95vw] max-w-3xl max-h-[95vh] overflow-y-auto" data-testid="wo-mat-req-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  Request Material · <span className="font-mono text-sm">{matReqFor.wo_number}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="text-xs text-slate-600 dark:text-slate-400 border rounded bg-slate-50 dark:bg-slate-900 p-2 mb-2">
                <div className="font-semibold text-foreground">{matReqFor.title}</div>
                {matReqFor.complaint_ticket_number && <div>Ticket · <span className="font-mono text-amber-700">{matReqFor.complaint_ticket_number}</span></div>}
              </div>
              <MaterialRequestPanel wo={matReqFor} />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}

function LinkedReportMaterials({ workOrder }) {
  // Fetch the linked service report(s) so the WO shows the materials
  // requested + issued on the SR (not only the ones issued via QuickCapture).
  const [rep, setRep] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Preferred: the WO carries `service_report_id` set on complete/from-complaint flows.
        if (workOrder.service_report_id) {
          const url = `${API}/service-reports?complaint_id=${workOrder.source_id || ''}`;
          const r = await axios.get(url, { withCredentials: true });
          if (cancelled) return;
          const match = (r.data || []).find(x => x.id === workOrder.service_report_id)
                     || (r.data || []).find(x => x.work_order_id === workOrder.id);
          setRep(match || null);
          return;
        }
        // Otherwise, if WO came from a complaint, pull SRs for that complaint.
        if (workOrder.source === 'complaint' && workOrder.source_id) {
          const r = await axios.get(`${API}/service-reports?complaint_id=${workOrder.source_id}`, { withCredentials: true });
          if (cancelled) return;
          const latest = (r.data || [])[0];
          setRep(latest || null);
        }
      } catch { /* silently ignore — WO view still renders */ }
    })();
    return () => { cancelled = true; };
  }, [workOrder.id, workOrder.service_report_id, workOrder.source_id, workOrder.source]);

  const needed = rep?.materials_needed || [];
  const used = rep?.materials_used || [];
  if (!needed.length && !used.length) return null;
  return (
    <div data-testid="wo-linked-sr-materials" className="border-t pt-3 space-y-2">
      <div className="text-xs font-semibold uppercase text-teal-700">From linked service report {rep?.id ? `· #${rep.id.slice(0, 8)}` : ''}</div>
      {needed.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-amber-700 mb-1">Materials Requested ({needed.length})</div>
          <ul className="text-xs space-y-0.5">
            {needed.map((m, i) => (
              <li key={i} className="flex flex-wrap gap-1 items-center py-0.5">
                <span className="flex-1">📦 <strong>{m.item}</strong>{m.brand ? ` · ${m.brand}` : ''}{m.size ? ` · ${m.size}` : ''} — {m.qty} {m.unit}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${m.status === 'issued' ? 'bg-blue-100 text-blue-700' : m.status === 'approved' ? 'bg-lime-100 text-lime-800' : m.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-slate-100 text-slate-600'}`}>{m.status || 'pending'}</span>
                {m.is_returnable && <span className="px-1.5 py-0.5 rounded text-[10px] bg-cyan-100 text-cyan-800">🔄 Returnable</span>}
                {m.returned_at && <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-800">↩ returned</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {used.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-1">Materials Used ({used.length})</div>
          <ul className="text-xs space-y-0.5 pl-4 list-disc">
            {used.map((m, i) => <li key={i}>{m.item_name || m.item} — {m.quantity ?? m.qty} {m.unit || ''}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function QuickCapture({ wo, onChanged }) {
  const [hours, setHours] = useState('');
  const [rate, setRate] = useState('');

  const addLabour = async () => {
    if (!hours) return;
    try {
      const r = await axios.post(`${API}/work-orders/${wo.id}/add-labour`, { hours: parseFloat(hours), hourly_rate: parseFloat(rate || '0') }, { withCredentials: true });
      toast.success('Labour added');
      setHours(''); setRate('');
      onChanged(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="text-xs font-semibold uppercase text-slate-500">Capture work</div>
      <div className="flex flex-wrap gap-2 items-end">
        <div><Label className="text-xs">Hours</Label><Input data-testid="qc-hours" type="number" step="0.25" className="w-24" value={hours} onChange={(e) => setHours(e.target.value)} /></div>
        <div><Label className="text-xs">Rate</Label><Input data-testid="qc-rate" type="number" step="0.5" className="w-24" value={rate} onChange={(e) => setRate(e.target.value)} /></div>
        <Button data-testid="qc-add-labour" size="sm" variant="outline" onClick={addLabour}><Clock className="w-3 h-3 mr-1" />Add Labour</Button>
      </div>
      <p className="text-[11px] text-slate-500 italic">
        Materials go through the &quot;Request material from store&quot; panel below → Engineer approves → Storekeeper issues.
      </p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// CompleteWorkOrderWizard — guided 3-step Complete flow.
//   1) Notes    — what was fixed / done
//   2) Photos   — evidence (multi-upload, optional)
//   3) Sign-off — tenant signature (typed) OR technician self-sign if no tenant
// Submits to POST /work-orders/{id}/complete which auto-creates the linked SR
// with all captured artefacts propagated.
// ─────────────────────────────────────────────────────────────────────────────
function CompleteWorkOrderWizard({ wo, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [notes, setNotes] = useState(wo.completion_notes || '');
  const [beforePhotos, setBeforePhotos] = useState(wo.before_photos || []);
  const [afterPhotos, setAfterPhotos] = useState(wo.after_photos || []);
  const [photoPhase, setPhotoPhase] = useState('after'); // 'before' | 'after'
  const [uploading, setUploading] = useState(false);
  const [tenantName, setTenantName] = useState('');
  const [saving, setSaving] = useState(false);
  const hasTenant = !!wo.tenant_id;

  const uploadPhoto = async (file) => {
    const form = new FormData();
    form.append('file', file);
    setUploading(true);
    try {
      const r = await axios.post(`${API}/upload`, form, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      if (photoPhase === 'before') setBeforePhotos((prev) => [...prev, r.data.url]);
      else setAfterPhotos((prev) => [...prev, r.data.url]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const removePhoto = (phase, idx) => {
    if (phase === 'before') setBeforePhotos((prev) => prev.filter((_, i) => i !== idx));
    else setAfterPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!notes.trim()) { toast.error('Please describe what was done in Step 1'); setStep(1); return; }
    if (hasTenant && !tenantName.trim()) { toast.error('Tenant signature is required'); setStep(3); return; }
    setSaving(true);
    try {
      const payload = {
        completion_notes: notes.trim(),
        before_photos: beforePhotos,
        after_photos: afterPhotos,
        photos: [...beforePhotos, ...afterPhotos], // legacy compat
        signature_data_url: tenantName.trim() ? `signed_by:${tenantName.trim()}` : null,
      };
      const r = await axios.post(`${API}/work-orders/${wo.id}/complete`, payload, { withCredentials: true });
      toast.success(`Work Order ${wo.wo_number} completed → Service Report generated`);
      onSuccess(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Complete failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[98vw] sm:w-[95vw] max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="complete-wo-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-teal-600" />
            Complete {wo.wo_number} — Generate Service Report
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 text-xs">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`flex items-center gap-1 ${step === s ? 'text-teal-700 font-bold' : step > s ? 'text-emerald-600' : 'text-slate-400'}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${step === s ? 'border-teal-600 bg-teal-50' : step > s ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300'}`}>
                {step > s ? <CheckCircle2 className="w-3 h-3" /> : s}
              </span>
              {s === 1 && 'Notes'}
              {s === 2 && 'Photos'}
              {s === 3 && 'Sign-off'}
              {s < 3 && <ChevronRight className="w-3 h-3 ml-1" />}
            </div>
          ))}
        </div>

        <div className="min-h-[260px] py-2">
          {step === 1 && (
            <div className="space-y-3">
              {/* Feb-2026 — surface the materials already issued to this WO so
                  the technician knows exactly what will auto-copy to the
                  Service Report. If nothing has been issued yet, we still
                  allow completion (some jobs use no materials at all). */}
              {(wo.materials_issued || []).length > 0 ? (
                <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-3" data-testid="wizard-materials-summary">
                  <div className="text-[11px] font-semibold uppercase text-purple-700 flex items-center gap-1 mb-1">
                    <Package className="w-3 h-3" /> Materials issued by storekeeper — will auto-copy to Service Report
                  </div>
                  <ul className="text-xs space-y-0.5 pl-4 list-disc text-slate-700">
                    {(wo.materials_issued || []).map((m, i) => (
                      <li key={i}>
                        <strong>{m.item_name}</strong> — {m.quantity} {m.unit || ''}
                        {m.mr_number ? <span className="text-slate-500"> · via <code className="font-mono">{m.mr_number}</code></span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="wizard-no-materials">
                  <div className="font-semibold text-slate-700 mb-0.5">No materials issued yet</div>
                  That&apos;s fine — you can still complete this Work Order if the job needed no store material. If you DID need materials, cancel this wizard, use the <span className="text-purple-700 font-semibold">Material</span> button to request, and complete once the storekeeper issues.
                </div>
              )}
              <Label className="flex items-center gap-1">Work Done <span className="text-rose-600">*</span></Label>
              <p className="text-[11px] text-slate-500">Describe the fault found, root cause, and what was fixed. This copies to the Service Report as the &quot;action taken&quot; narrative.</p>
              <Textarea data-testid="wizard-notes" rows={7} placeholder="e.g. AC compressor was tripping on overload. Cleaned coils, checked refrigerant pressure (within spec), reset breaker. Unit running normally." value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                Photos <span className="text-[11px] font-normal text-slate-500">— evidence at start & completion</span>
              </Label>
              <div className="inline-flex rounded-lg border bg-slate-50 p-1 text-xs" data-testid="wizard-photo-phase-toggle">
                <button
                  type="button"
                  onClick={() => setPhotoPhase('before')}
                  className={`px-3 py-1 rounded-md font-semibold transition ${photoPhase === 'before' ? 'bg-amber-500 text-white shadow' : 'text-slate-600 hover:bg-white'}`}
                  data-testid="wizard-tab-before"
                >📷 Before ({beforePhotos.length})</button>
                <button
                  type="button"
                  onClick={() => setPhotoPhase('after')}
                  className={`px-3 py-1 rounded-md font-semibold transition ${photoPhase === 'after' ? 'bg-emerald-600 text-white shadow' : 'text-slate-600 hover:bg-white'}`}
                  data-testid="wizard-tab-after"
                >✅ After ({afterPhotos.length})</button>
              </div>
              <p className="text-[11px] text-slate-500 italic">
                {photoPhase === 'before'
                  ? 'Capture the fault / initial condition BEFORE starting work.'
                  : 'Capture the completed / repaired condition AFTER finishing.'}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {(photoPhase === 'before' ? beforePhotos : afterPhotos).map((url, i) => (
                  <div key={i} className="relative group">
                    <img src={`${API.replace('/api', '')}${url}`} alt="" className="w-full h-24 object-cover rounded border" />
                    <span className={`absolute top-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${photoPhase === 'before' ? 'bg-amber-500 text-white' : 'bg-emerald-600 text-white'}`}>{photoPhase}</span>
                    <button type="button" onClick={() => removePhoto(photoPhase, i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label className="flex items-center justify-center h-24 border-2 border-dashed border-slate-300 rounded cursor-pointer hover:bg-slate-50 text-slate-500 text-xs">
                  <input data-testid="wizard-photo-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadPhoto(e.target.files[0]); e.target.value = ''; }} />
                  {uploading ? <span>Uploading…</span> : <span className="flex flex-col items-center gap-1"><Camera className="w-5 h-5" /> Add {photoPhase}</span>}
                </label>
              </div>
              <p className="text-[11px] text-slate-500 italic">On mobile, tapping &quot;Add&quot; opens the camera directly.</p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="rounded border p-3 bg-slate-50 text-xs space-y-1">
                <div><span className="text-slate-500">Work done:</span> {notes.slice(0, 140)}{notes.length > 140 ? '…' : ''}</div>
                <div><span className="text-slate-500">Before photos:</span> {beforePhotos.length} · <span className="text-slate-500">After photos:</span> {afterPhotos.length}</div>
                <div><span className="text-slate-500">Materials to include in SR:</span> {(wo.materials_issued || []).length === 0 ? <em>none</em> : `${(wo.materials_issued || []).length} item(s) — ${(wo.materials_issued || []).map(m => m.item_name).join(', ').slice(0, 100)}${(wo.materials_issued || []).map(m => m.item_name).join(', ').length > 100 ? '…' : ''}`}</div>
              </div>
              {hasTenant ? (
                <>
                  <Label className="flex items-center gap-1"><PenLine className="w-4 h-4 text-teal-700" />Tenant Confirmation <span className="text-rose-600">*</span></Label>
                  <p className="text-[11px] text-slate-500">Ask the tenant to confirm the work is complete by typing their full name below. This is captured as their signature on the Service Report.</p>
                  <Input data-testid="wizard-tenant-name" placeholder="Tenant's full name" value={tenantName} onChange={(e) => setTenantName(e.target.value)} />
                </>
              ) : (
                <div className="p-3 rounded bg-blue-50 border border-blue-200 text-xs">
                  This work order has no linked tenant. Submitting will mark it complete and generate a Service Report signed off by you as the technician.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between pt-3 border-t">
          <Button variant="outline" data-testid="wizard-back" onClick={() => step > 1 ? setStep(step - 1) : onClose()}>
            {step > 1 ? (<><ChevronLeft className="w-4 h-4 mr-1" />Back</>) : 'Cancel'}
          </Button>
          {step < 3 ? (
            <Button data-testid="wizard-next" onClick={() => setStep(step + 1)} disabled={step === 1 && !notes.trim()} className="bg-teal-600 hover:bg-teal-700 text-white">
              Next<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button data-testid="wizard-submit" onClick={submit} disabled={saving || (hasTenant && !tenantName.trim())} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCircle2 className="w-4 h-4 mr-1" />{saving ? 'Completing…' : 'Complete → Generate Service Report'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// MaterialRequestPanel — technician-facing "request from store" panel inside
// the WO view. Semantically material requests belong here (before work), not
// on the Service Report (which is the post-work document).
// ─────────────────────────────────────────────────────────────────────────────
function MaterialRequestPanel({ wo }) {
  const [stocks, setStocks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [lines, setLines] = useState([]);
  const [mode, setMode] = useState('stock'); // 'stock' | 'custom' — explicit so the toggle works before typing
  const [draft, setDraft] = useState({ stock_item_id: '', item_name: '', brand: '', size: '', qty: '', unit: '', notes: '', is_returnable: false });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const isCustom = mode === 'custom';

  const refresh = React.useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/stock-items`, { withCredentials: true }),
        axios.get(`${API}/material-requests?work_order_id=${wo.id}`, { withCredentials: true }),
      ]);
      setStocks(s.data || []);
      setRequests(r.data || []);
    } catch { /* silent */ }
  }, [wo.id]);
  useEffect(() => { refresh(); }, [refresh]);

  const buildLineFromDraft = () => {
    const item_name = draft.item_name || (stocks.find(s => s.id === draft.stock_item_id)?.item_name);
    if (!item_name || !draft.qty) return null;
    return {
      stock_item_id: draft.stock_item_id || undefined,
      item_name,
      brand: draft.brand || undefined,
      size: draft.size || undefined,
      qty: parseFloat(draft.qty),
      unit: draft.unit || stocks.find(s => s.id === draft.stock_item_id)?.unit || '',
      is_returnable: draft.is_returnable,
      notes: draft.notes || undefined,
    };
  };
  const addLine = () => {
    const l = buildLineFromDraft();
    if (!l) return toast.error('Pick an item / enter a name and quantity');
    setLines([...lines, l]);
    setDraft({ stock_item_id: '', item_name: '', brand: '', size: '', qty: '', unit: '', notes: '', is_returnable: false });
  };
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const submit = async () => {
    let toSend = lines;
    const pending = buildLineFromDraft();
    if (pending) toSend = [...lines, pending];
    if (toSend.length === 0) return toast.error('Add at least one line');
    setBusy(true);
    try {
      await axios.post(`${API}/work-orders/${wo.id}/material-requests`, { lines: toSend, reason }, { withCredentials: true });
      toast.success('Material request sent — awaiting Engineer approval');
      setLines([]); setReason('');
      setDraft({ stock_item_id: '', item_name: '', brand: '', size: '', qty: '', unit: '', notes: '', is_returnable: false });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request failed');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="wo-material-form" className="space-y-3 border-t pt-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-bold uppercase text-purple-700 flex items-center gap-1.5" data-testid="wo-material-form-header">
          <Package className="w-4 h-4" />Request material from store
          <span className="text-[10px] font-normal normal-case text-slate-500">— for THIS Work Order (not a Service Report)</span>
        </div>
        {requests.length > 0 && (
          <span className="text-xs text-slate-500">{requests.length} request(s) linked · <a href="/material-requests" className="text-purple-600 hover:underline">view queue</a></span>
        )}
      </div>

      {/* Past requests on this WO */}
      {requests.map(mr => (
        <div key={mr.id} className="text-xs p-2 rounded bg-slate-50 border border-slate-200" data-testid={`wo-mr-row-${mr.mr_number}`}>
          <div className="font-mono font-semibold">{mr.mr_number} <span className="ml-2 text-[10px] uppercase text-purple-700">{mr.status}</span></div>
          <ul className="pl-4 mt-1 space-y-0.5">
            {(mr.lines || []).map((l, i) => (
              <li key={i} className="flex justify-between">
                <span>{l.item_name} — {l.qty} {l.unit || ''}{l.is_returnable ? ' 🔁' : ''}</span>
                <span className="text-slate-500">{l.status}{l.pr_number ? (
                  <>
                    {' → '}
                    <a href={`/purchase-requests?highlight=${encodeURIComponent(l.pr_number)}&from=${encodeURIComponent(mr.mr_number)}`} className="text-purple-600 hover:underline" title="Open the linked Purchase Request">{l.pr_number}</a>
                  </>
                ) : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {/* New request line editor */}
      <div className="border-2 border-dashed border-purple-200 rounded-lg p-3 bg-purple-50/30 space-y-3" data-testid="wo-material-form-editor">
        {/* Mode selector — makes stock vs custom EXPLICIT */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="font-semibold text-slate-700">Item source:</span>
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name="mr-source"
              checked={mode === 'stock'}
              onChange={() => { setMode('stock'); setDraft(d => ({ ...d, item_name: '', brand: '', size: '' })); }}
              data-testid="mr-source-stock"
            />
            <span>📦 From store stock</span>
          </label>
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input
              type="radio"
              name="mr-source"
              checked={mode === 'custom'}
              onChange={() => { setMode('custom'); setDraft(d => ({ ...d, stock_item_id: '' })); }}
              data-testid="mr-source-custom"
            />
            <span>🛒 Custom / not in stock <span className="text-slate-500">(→ auto Purchase Request)</span></span>
          </label>
        </div>

        {/* Stock picker (visible only when mode is stock) */}
        {!isCustom && (
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[240px]">
              <Label className="text-xs">Pick from stock</Label>
              <Select value={draft.stock_item_id} onValueChange={(v) => {
                const s = stocks.find(x => x.id === v);
                setDraft(d => ({ ...d, stock_item_id: v, item_name: s?.item_name || '', unit: s?.unit || d.unit, brand: s?.brand || '', size: s?.size || '' }));
              }}>
                <SelectTrigger data-testid="mr-draft-stock"><SelectValue placeholder="Choose item…" /></SelectTrigger>
                <SelectContent>{stocks.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.item_name}{s.brand ? ` · ${s.brand}` : ''}{s.size ? ` · ${s.size}` : ''} (stock: {s.current_quantity ?? 0} {s.unit || ''})</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Qty</Label><Input data-testid="mr-draft-qty" className="w-24" type="number" step="any" value={draft.qty} onChange={(e) => setDraft({ ...draft, qty: e.target.value })} /></div>
            <div><Label className="text-xs">Unit</Label><Input data-testid="mr-draft-unit" className="w-20" value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} placeholder="pcs" /></div>
            <label className="flex items-center gap-1 text-xs mt-4 select-none">
              <input type="checkbox" checked={draft.is_returnable} onChange={(e) => setDraft({ ...draft, is_returnable: e.target.checked })} data-testid="mr-draft-returnable" />
              Returnable (loan tool)
            </label>
          </div>
        )}

        {/* Custom-item form (visible only when mode is custom) — full spec fields */}
        {isCustom && (
          <div className="space-y-2">
            <div className="p-2 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-800">
              <strong>Not in stock?</strong> Fill the specs below. Engineer approves → <strong>Storekeeper auto-raises a Purchase Request</strong> → Purchase Order → GRN → material issued to this WO. You&apos;ll receive notification when ready to collect.
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="col-span-2"><Label className="text-xs">Item name <span className="text-rose-600">*</span></Label><Input data-testid="mr-draft-item-name" value={draft.item_name} onChange={(e) => setDraft({ ...draft, item_name: e.target.value })} placeholder="e.g. LED tube light" /></div>
              <div><Label className="text-xs">Brand / Model</Label><Input data-testid="mr-draft-brand" value={draft.brand} onChange={(e) => setDraft({ ...draft, brand: e.target.value })} placeholder="e.g. Philips, Havells" /></div>
              <div><Label className="text-xs">Size / Spec</Label><Input data-testid="mr-draft-size" value={draft.size} onChange={(e) => setDraft({ ...draft, size: e.target.value })} placeholder="e.g. 20W, 4ft, 6500K" /></div>
              <div><Label className="text-xs">Qty <span className="text-rose-600">*</span></Label><Input data-testid="mr-draft-qty" type="number" step="any" value={draft.qty} onChange={(e) => setDraft({ ...draft, qty: e.target.value })} /></div>
              <div><Label className="text-xs">Unit</Label><Input data-testid="mr-draft-unit" value={draft.unit} onChange={(e) => setDraft({ ...draft, unit: e.target.value })} placeholder="pcs, m, kg" /></div>
              <label className="flex items-center gap-1 text-xs mt-6 select-none col-span-2">
                <input type="checkbox" checked={draft.is_returnable} onChange={(e) => setDraft({ ...draft, is_returnable: e.target.checked })} data-testid="mr-draft-returnable" />
                Returnable (loan tool)
              </label>
            </div>
          </div>
        )}

        <Input data-testid="mr-draft-notes" placeholder="Extra notes about this line (optional)…" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />

        <div className="flex justify-end">
          <Button data-testid="mr-add-line-btn" size="sm" variant="outline" onClick={addLine}>+ Add another line</Button>
        </div>

        {lines.length > 0 && (
          <div className="border-t pt-2 space-y-1">
            <div className="text-xs font-semibold text-slate-600">Lines to send ({lines.length}):</div>
            {lines.map((l, i) => (
              <div key={i} className="text-xs flex justify-between items-center py-0.5" data-testid={`mr-pending-line-${i}`}>
                <span>
                  {l.stock_item_id ? '📦' : '🛒'} <strong>{l.item_name}</strong>
                  {l.brand ? ` · ${l.brand}` : ''}
                  {l.size ? ` · ${l.size}` : ''}
                  {' — '}{l.qty} {l.unit}
                  {l.is_returnable ? ' 🔁' : ''}
                  {l.notes ? ` · "${l.notes}"` : ''}
                </span>
                <button className="text-rose-600 hover:text-rose-800 text-xs" onClick={() => removeLine(i)}>Remove</button>
              </div>
            ))}
          </div>
        )}

        <div className="border-t pt-2 space-y-1">
          <Input placeholder="Overall reason / justification (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="mr-reason" />
          <Button
            data-testid="mr-submit-btn"
            size="sm" className="bg-purple-600 hover:bg-purple-700 text-white w-full mt-1" disabled={busy}
            onClick={submit}
          >
            {busy ? 'Sending…' : `Send request${(lines.length + (draft.item_name || draft.stock_item_id ? 1 : 0)) > 0 ? ` (${lines.length + (draft.item_name || draft.stock_item_id ? 1 : 0)} item${(lines.length + (draft.item_name || draft.stock_item_id ? 1 : 0)) > 1 ? 's' : ''})` : ''} to Engineer for approval`}
          </Button>
          <p className="text-[10px] text-slate-500 italic leading-relaxed">
            <strong>Flow:</strong> Engineer approves → <strong>In stock:</strong> Storekeeper issues directly · <strong>Custom / low stock:</strong> Storekeeper raises Purchase Request → Purchase Order → GRN → material issued to your WO. Rejections come with a reason.
          </p>
        </div>
      </div>
    </div>
  );
}
